import { useEffect, useRef, useState } from "react";
import axios from "axios";
import { Link } from "react-router-dom";
import {
    motion,
    useMotionValue,
    useScroll,
    useSpring,
    useTransform,
} from "framer-motion";
import Lenis from "lenis";
import { INSIGHTS } from "@/pages/InsightPost";
import {
    ArrowUpRight,
    Download,
    Mail,
    Linkedin,
    GraduationCap,
    BookOpen,
    Send,
    MapPin,
    Phone,
    ChevronLeft,
    ChevronRight,
} from "lucide-react";
import { toast } from "sonner";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const RESUME_URL =
    "https://customer-assets-eiarnc6j.emergentagent.net/job_energy-materials-pm/artifacts/p2x53e48_Hanan%20Hakim_CV_RnD%20Scientist%202026.pdf";
const LINKEDIN_URL = "https://www.linkedin.com/in/hananhakim/";
const SCHOLAR_URL =
    "https://scholar.google.com/citations?user=PpLAECwAAAAJ&hl=id";
const RESEARCHGATE_URL =
    "https://www.researchgate.net/profile/Hanan_Hakim2?ev=hdr_xprf";
const EMAIL = "work@hananhakim.com";

const HEADSHOT_URL =
    "https://customer-assets.emergentagent.com/job_energy-materials-pm/artifacts/z5wo4zu7_Photoshoot%20Hanan%20Hakim.png";

const NAV_LINKS = [
    { id: "impact", label: "Impact" },
    { id: "about", label: "About" },
    { id: "portfolio", label: "Portfolio" },
    { id: "leadership", label: "Leadership" },
    { id: "publications", label: "Publications" },
    { id: "recruiters", label: "Connect" },
];

const METRICS = [
    {
        value: "£161–484M",
        label: "Revenue Linked",
        body: "Quantified commercial stakes of a 1–3% attrition shift, anchoring a £30k investor seed-raise.",
    },
    {
        value: "82%",
        label: "Root Cause Isolated",
        body: "Diagnosed a client attrition crisis on 2.9M records via a hypothesis-driven issue tree.",
    },
    {
        value: "£49–66/MWh",
        label: "Grid Storage Pricing",
        body: "Architected the techno-economic module for a DESNZ-commissioned, 3-university UK climate report.",
    },
    {
        value: "~£550k",
        label: "Advised Engagements",
        body: "Owned full client mandate across Forbes Global 2000 firms in chemicals, beauty and aerospace-defence.",
    },
];

const COMPETENCIES = [
    "Strategy Consulting",
    "Project Management",
    "Energy Transition",
    "Materials & Circularity",
    "Techno-Economic Analysis",
    "Market Sizing & Due Diligence",
    "Financial Modelling (LCOS / LCOE)",
    "Python · Pandas · scikit-learn",
    "SQL · Advanced Excel",
    "Issue Trees & Weighted Frameworks",
    "Stakeholder Communication",
    "Cross-Cultural Team Leadership",
];

const EDUCATION = [
    {
        degree_short: "MSc",
        school: "Imperial College London",
        detail: "Advanced Materials Science · Distinction in Machine Learning for Materials.",
        honour: "Merit · DIC",
        years: "2024 — 2025",
        domain: "imperial.ac.uk",
        initials: "IC",
    },
    {
        degree_short: "Professional",
        school: "Stanford University",
        detail: "Energy Innovation & Emerging Technologies · Renewables, Storage, Hydrogen, Strategy.",
        honour: "Professional Ed.",
        years: "2025",
        domain: "stanford.edu",
        initials: "SU",
    },
    {
        degree_short: "BSc",
        school: "Sepuluh Nopember Institute of Technology",
        detail: "Chemistry · Thesis on chitosan-templated mesoporous hydroxyapatite.",
        honour: "Cum Laude · Top 10%",
        years: "2017 — 2021",
        domain: "its.ac.id",
        initials: "ITS",
        logoUrl:
            "https://customer-assets.emergentagent.com/job_energy-materials-pm/artifacts/bf0zmfzo_Sepuluh_Nopember_Institute_of_Technology_Logo.png",
    },
];

const TRUSTED_BY = [
    { name: "Imperial College London", domain: "imperial.ac.uk", initials: "IC" },
    { name: "Henry Royce Institute", domain: "royce.ac.uk", initials: "HR" },
    { name: "180 Degrees Consulting", domain: "180dc.org", initials: "180" },
    { name: "Stanford University", domain: "stanford.edu", initials: "SU" },
    { name: "UK DESNZ", domain: "gov.uk", initials: "GOV" },
    { name: "Nano Center Indonesia", domain: "nanocenter.id", initials: "NC" },
    {
        name: "Atomalyst Group",
        domain: null,
        initials: "AT",
        logoUrl:
            "https://customer-assets.emergentagent.com/job_energy-materials-pm/artifacts/1pu6si3h_atomalyst_logo.jpeg",
    },
];

const LEADERSHIP = [
    {
        n: "01",
        name: "23rd Indonesian Scholars International Convention",
        role: "Vice Project Leader",
        years: "2024 — 2025",
        location: "London, UK",
        detail:
            "Turned around a stalled 30-person team to deliver a 500+ attendee convention 8–12% under a £6.5k budget; raised £7.5k in sponsorship.",
        domain: "ppiuk.org",
        initials: "ISIC",
        links: [
            { label: "Recap on Instagram", href: "https://www.instagram.com/p/DLqWD28o7QA/" },
        ],
    },
    {
        n: "02",
        name: "YoutHive Indonesia",
        role: "Founder & President",
        years: "2020 — 2022",
        location: "Jakarta, ID",
        detail:
            "Launched a youth-mentoring programme with a 7%-selective intake (10 of 150). All 10 mentees secured university placement — 100% conversion.",
        domain: null,
        initials: "YH",
        logoUrl:
            "https://customer-assets.emergentagent.com/job_energy-materials-pm/artifacts/3v04erqw_logowithouttext.png",
        links: [
            { label: "@youthive.id", href: "https://www.instagram.com/youthive.id/" },
        ],
    },
    {
        n: "03",
        name: "Bumi Buddies",
        role: "External Affairs Lead",
        years: "2020 — 2022",
        location: "Surabaya, ID",
        detail:
            "Mobilised partnerships with 5 NGOs and school networks to deliver climate and plastic-waste education to 800+ students across 10 institutions.",
        domain: null,
        initials: "BB",
        logoUrl:
            "https://customer-assets.emergentagent.com/job_energy-materials-pm/artifacts/yifq3ewe_43353644_252904485420609_6872312750115127296_n.jpg",
        links: [
            { label: "bumibuddies.org", href: "https://bumibuddies.org/" },
            { label: "@bumibuddies", href: "https://www.instagram.com/bumibuddies/" },
        ],
    },
    {
        n: "04",
        name: "IYOIN Local Chapter Surabaya",
        role: "Deputy Executive Director",
        years: "2019 — 2021",
        location: "Surabaya, ID",
        detail:
            "Coordinated 5 departments and led 2 flagship teams — Crazy Power Millennials & Tailoring Your Potential CV — scaling external partnerships to 650+ participants.",
        domain: null,
        initials: "IY",
        logoUrl:
            "https://customer-assets.emergentagent.com/job_energy-materials-pm/artifacts/4eqm36ol_13721055_1658864504437215_1158145480_a.jpg",
        links: [
            { label: "@iyoinsby", href: "https://www.instagram.com/iyoinsby/" },
        ],
    },
];

const CASES = [
    {
        n: "01",
        slug: "lyncfit",
        tag: "Strategy · UK Higher Education · 180 Degrees Consulting",
        title: "Translating LyncFit into a UK HE engagement strategy",
        challenge:
            "A wellbeing-tech startup needed a credible wedge into a £40B+ UK Higher Education market where 28% of undergraduates consider dropping out and incumbents only solve the admin layer, not engagement.",
        strategy:
            "Owned the multi-stakeholder GTM workstream at 180 Degrees Consulting (Imperial × QMUL). Built the society-first wedge thesis, a 4-phase rollout (Society → Wellbeing → Retention → Insights/Analytics), tiered university segmentation and a willingness-to-pay model (£1–5 / student / year).",
        result:
            "Python-ML retention simulator projects 5.8k–17.4k UK students retained annually with a 1–3% engagement lift — protecting £161M–£484M in tuition revenue and anchoring a £500k Q4-2026 seed close.",
    },
    {
        n: "02",
        slug: "tara",
        tag: "Innovation · AI for Development · Geneva Challenge 2025 Global Finalist",
        title: "TARA — closing the migrant-student transition gap",
        challenge:
            "Indonesia spends ~US$962M / year sending its best students to UK universities, but a preventable transition gap (housing, NHS, visas, wellbeing) erodes academic outcomes and 'brain waste' wipes out ROI.",
        strategy:
            "Co-led a 5-person Imperial × UCL × Hamburg team to design TARA — a bilingual, WhatsApp-native AI chatbot leveraging 90.9% messenger penetration. Built the cost model, multi-level intervention architecture and a partnership stack with PPI UK, LPDP and host universities.",
        result:
            "Cost-per-interaction <US$0.50 vs. US$15–25 human support; ~£5 per student reached; protects ~US$96k per scholar-year national investment. Selected as a Geneva Challenge 2025 Global Finalist from 100+ teams.",
    },
    {
        n: "03",
        slug: "desnz",
        tag: "Energy Transition · UK Government · Imperial × UCL × Cambridge",
        title: "Pricing grid-scale storage for DESNZ",
        challenge:
            "The UK Department for Energy Security & Net Zero needed defensible, decision-grade costs for grid-scale storage across competing technologies — under tight stakeholder scrutiny.",
        strategy:
            "Architected the techno-economic module of the 3-university DESNZ-commissioned climate report. Synthesised regulatory and TRL dynamics across five jurisdictions; stress-tested ~10 sensitivity inputs.",
        result:
            "Delivered a £49–66/MWh pricing band that shaped the leadership team's storage-technology positioning in the final report to DESNZ.",
    },
    {
        n: "04",
        slug: "atomalyst",
        tag: "Sustainability · Forbes Global 2000 · Atomalyst",
        title: "A multi-billion plastics-substitution play",
        challenge:
            "A Forbes Global 2000 manufacturer was over-engineering polyolefin parts and exposed to mounting circularity regulation, with no clear path to a credible bio-based alternative.",
        strategy:
            "Founded and scaled Atomalyst Group to a 4-person advisory; structured the plastics-substitution thesis, ran TRL/risk diligence with senior R&D and procurement, and sized the chitosan value chain.",
        result:
            "Mapped a path to a multi-billion-dollar chitosan market by 2033, surfacing ~20% in addressable cost savings; frameworks were adopted into the client's procurement process across ~£550k of mandates.",
    },
    {
        n: "05",
        slug: "thesis",
        tag: "Electrochemical Catalysis · MSc · Henry Royce Institute",
        title:
            "Composition engineering of Ni-Co hydroxide for glycerol oxidation",
        challenge:
            "Crude glycerol — ~10% of biodiesel output by weight — has historically been waste. Earth-abundant Ni-Co hydroxides are known to oxidise it electrochemically in alkaline media, but the optimal Ni : Co ratio for activity AND durability was unresolved.",
        strategy:
            "Fabricated five Ni-Co bimetallic hydroxide compositions by chronopotentiostatic electrodeposition on FTO substrates, then characterised each through CV, LSV, EIS, Tafel, chronoamperometry, XPS (survey + Ni 2p / Co 2p / O 1s), SEM and EDX.",
        result:
            "Ni₀.₀₇₅Co₀.₀₂₅(OH)₂ emerged as the optimal composition — charge-transfer resistance dropped from ~10.9 kΩ (pure Ni(OH)₂) to ~1.01 kΩ. Established Ni as the active centre and Co as an electronic promoter; open-source workflow adopted as a standard Royce Institute protocol.",
    },
];

const PUBLICATIONS = [
    {
        year: "2025",
        title:
            "Green synthesis of hydroxyapatite from oyster shells for dye-wastewater removal",
        venue: "AIP Conference Proceedings · First-authored",
    },
    {
        year: "2023",
        title:
            "Review on advanced materials for sustainable energy and waste valorisation",
        venue: "Journal of Alloys & Compounds",
    },
    {
        year: "—",
        title: "Two additional peer-reviewed contributions",
        venue: "Materials chemistry · sustainable synthesis",
    },
];

const AWARDS = [
    {
        year: "2025",
        title: "Geneva Challenge — Global Finalist",
        detail: "Top finalist from 100+ international teams.",
    },
    {
        year: "2024",
        title: "Bicentenary Global Futures Scholarship",
        detail: "Offer from the University of Manchester.",
    },
    {
        year: "2025",
        title: "McKinsey Forward Programme",
        detail: "Selected participant — leadership & consulting toolkit.",
    },
    {
        year: "2020",
        title: "BUMI Leadership Scholar — Top 6",
        detail: "Selected as one of 150+ candidates nationally for the BUMI Scholars programme.",
    },
    {
        year: "2022",
        title: "Highest Performer — Nano Center Indonesia",
        detail: "Recognised by the Applied Physics & Chemistry division for top R&D delivery during a key product-development cycle.",
    },
    {
        year: "2019",
        title: "Bronze Medal — Wastewater Innovation",
        detail: "Advanced Innovation Global Competition (AIGC), Nanyang Technological University, Singapore.",
    },
];

const AWARDS_GALLERY = [
    {
        slug: "aigc-2019",
        year: "2019",
        title: "Bronze Medal · AIGC Singapore",
        location: "Nanyang Technological University, Singapore",
        image: "https://customer-assets.emergentagent.com/job_energy-materials-pm/artifacts/9q4syu8p_75394799_2992746824157358_3786811736477138944_n.jpg",
        description:
            "Bronze at the Advanced Innovation Global Competition for a wastewater innovation project — my first international podium and the moment that made me commit to chemistry-for-impact as a career direction.",
    },
    {
        slug: "bumi-2020",
        year: "2020",
        title: "BUMI Leadership Scholar — Top 6",
        location: "BUMI Foundation, Indonesia",
        image: "/awards/cert_02.jpg",
        description:
            "Selected from 150+ national candidates for the BUMI Scholars programme — among the top 6 graded Summa Cumlaude (91–100). First formal recognition of the leadership track alongside research output.",
    },
    {
        slug: "nano-2022",
        year: "2022",
        title: "Highest Performer · Nano Center Indonesia",
        location: "Applied Physics & Chemistry · Nano Center Indonesia",
        image: "https://customer-assets.emergentagent.com/job_energy-materials-pm/artifacts/cuh7qo1a_1659925779021.jpeg",
        description:
            "Top performer recognition during a key product-development cycle, awarded by the Applied Physics & Chemistry team for R&D delivery on functional nanomaterials.",
    },
    {
        slug: "mckinsey-2025",
        year: "2025",
        title: "McKinsey Forward Programme",
        location: "McKinsey & Company",
        image: "/awards/mckinsey_forward.jpg",
        description:
            "Selected for McKinsey's leadership development programme — a curated curriculum in strategic problem-solving, executive communication, and digital fluency built around the firm's consulting toolkit.",
    },
    {
        slug: "stanford-2025",
        year: "2025",
        title: "Stanford Energy Innovation — Professional Education",
        location: "Stanford Engineering Center for Global & Online Education",
        image: "/awards/stanford_01.jpg",
        description:
            "Six-course professional programme covering hydrogen economics, clean-energy strategy, building energy efficiency, sustainability leadership and the economics of the energy transition.",
    },
    {
        slug: "msc-viva-2025",
        year: "2025",
        title: "MSc Imperial — Thesis Viva",
        location: "Department of Materials, Imperial College London",
        image: "https://customer-assets.emergentagent.com/job_energy-materials-pm/artifacts/ftzygdem_1758481088126.jpeg",
        description:
            "Defending the MSc thesis at Imperial's Department of Materials — ranked 1st in the UK and Europe, 2nd in the world (QS 2026). The work that became the published case study on Ni-Co hydroxide for glycerol oxidation.",
    },
    {
        slug: "isic-2025",
        year: "2025",
        title: "23rd Indonesian Scholars International Convention",
        location: "Imperial College London · PPI UK",
        image: "https://customer-assets.emergentagent.com/job_energy-materials-pm/artifacts/wvla54o5_1758481087921.jpeg",
        description:
            "Vice Project Leader for the 23rd ISIC convention held at Imperial College London — turned around a stalled 30-person team to deliver a 500+ attendee event 8–12% under budget while raising £7.5k in sponsorship.",
    },
];

const KEYWORDS = [
    "Strategy Consultant",
    "Project Manager",
    "Sustainability Lead",
    "Energy Transition Analyst",
    "Materials Science Strategist",
    "Techno-Economic Analyst",
    "ESG & Decarbonisation",
    "Grid-Scale Storage Strategy",
    "Climate Policy & Regulation",
    "Innovation Consulting",
    "Imperial College London MSc",
    "Forbes Global 2000 Advisor",
    "Due Diligence & Market Sizing",
    "Python · SQL · Financial Modelling",
];

// ───────────────────────────────────────────────────────────────────
// Reusable bits
// ───────────────────────────────────────────────────────────────────

const Overline = ({ children, accent = false, className = "" }) => (
    <span
        className={`font-mono text-xs uppercase tracking-[0.22em] ${
            accent ? "text-sienna" : "text-ink-soft"
        } ${className}`}
    >
        {children}
    </span>
);

const FadeUp = ({ children, delay = 0, className = "" }) => (
    <motion.div
        initial={{ opacity: 0, y: 22 }}
        whileInView={{ opacity: 1, y: 0 }}
        viewport={{ once: true, margin: "-80px" }}
        transition={{ duration: 0.7, delay, ease: [0.2, 0.7, 0.2, 1] }}
        className={className}
    >
        {children}
    </motion.div>
);

// Masked heading reveal — copy slides up from behind an overflow mask.
const MaskReveal = ({ children, delay = 0, className = "" }) => (
    <div className={`overflow-hidden ${className}`}>
        <motion.div
            className="will-change-transform"
            initial={{ y: "108%" }}
            whileInView={{ y: 0 }}
            viewport={{ once: true, margin: "-60px" }}
            transition={{ duration: 0.9, delay, ease: [0.16, 1, 0.3, 1] }}
        >
            {children}
        </motion.div>
    </div>
);

// Numbered manifesto chapter header — ghost numeral + overline + masked title.
const ChapterHead = ({
    index,
    overline,
    dark = false,
    titleClassName = "",
    children,
}) => (
    <div className="relative">
        <span
            aria-hidden="true"
            className={`pointer-events-none select-none absolute -top-12 md:-top-16 -left-1 md:-left-2 font-serif text-[5.5rem] md:text-[8rem] leading-none ${
                dark ? "text-ghost-dark" : "text-ghost"
            }`}
        >
            {index}
        </span>
        <Overline accent>{overline}</Overline>
        <MaskReveal className="mt-4">
            <h2
                className={`font-serif text-4xl md:text-5xl leading-[1.05] ${
                    dark ? "text-bone" : "text-ink"
                } ${titleClassName}`}
            >
                {children}
            </h2>
        </MaskReveal>
    </div>
);

// Thin sienna scroll-progress bar pinned above the nav.
const ScrollProgress = () => {
    const { scrollYProgress } = useScroll();
    const scaleX = useSpring(scrollYProgress, {
        stiffness: 120,
        damping: 24,
        mass: 0.4,
    });
    return (
        <motion.div
            data-testid="scroll-progress"
            className="fixed top-0 left-0 right-0 h-[2px] bg-sienna origin-left z-[70]"
            style={{ scaleX }}
        />
    );
};

const MARQUEE_ITEMS = [
    "Decision-grade strategy",
    "Energy transition",
    "Materials & circularity",
    "Boardroom-ready narratives",
    "Data-rooted analysis",
    "Forbes Global 2000 advisory",
];

// One slow editorial marquee — oversized serif ticker beneath the hero.
const EditorialMarquee = () => (
    <section
        aria-hidden="true"
        data-testid="editorial-marquee"
        className="relative border-t border-line py-10 md:py-14 overflow-hidden"
    >
        <div className="flex whitespace-nowrap marquee-slow will-change-transform">
            {[0, 1].map((half) => (
                <div key={half} className="flex shrink-0 items-center">
                    {MARQUEE_ITEMS.map((t, i) => (
                        <span key={i} className="flex items-center">
                            <span
                                className={`font-serif text-4xl md:text-6xl leading-none mx-8 md:mx-12 ${
                                    i % 2 ? "italic text-sienna" : "text-ink"
                                }`}
                            >
                                {t}
                            </span>
                            <span className="text-sienna text-sm md:text-base">
                                ◆
                            </span>
                        </span>
                    ))}
                </div>
            ))}
        </div>
    </section>
);

// Logo with explicit URL > DuckDuckGo favicon > Google favicon > monogram tile fallback.
const Logo = ({ logoUrl, domain, initials, name, size = 44, mono = false }) => {
    const [stage, setStage] = useState(logoUrl ? -1 : 0); // -1=direct url, 0=ddg, 1=google, 2=monogram
    useEffect(() => {
        setStage(logoUrl ? -1 : 0);
    }, [logoUrl, domain]);
    const src =
        stage === -1
            ? logoUrl
            : stage === 0 && domain
              ? `https://icons.duckduckgo.com/ip3/${domain}.ico`
              : stage === 1 && domain
                ? `https://www.google.com/s2/favicons?domain=${domain}&sz=128`
                : null;
    return (
        <div
            title={name}
            aria-label={name}
            style={{ width: size, height: size }}
            className="relative shrink-0 overflow-hidden border border-line bg-bone flex items-center justify-center"
        >
            {src ? (
                <img
                    src={src}
                    alt={`${name} logo`}
                    onError={() => setStage((s) => (s < 0 ? 0 : s + 1))}
                    className={`w-full h-full object-contain p-1.5 ${
                        mono ? "grayscale contrast-[1.05]" : ""
                    }`}
                    loading="lazy"
                />
            ) : (
                <span className="font-serif text-ink text-[14px] tracking-tight leading-none">
                    {initials}
                </span>
            )}
        </div>
    );
};

// ───────────────────────────────────────────────────────────────────
// NAV
// ───────────────────────────────────────────────────────────────────

const Nav = () => {
    const [scrolled, setScrolled] = useState(false);
    useEffect(() => {
        const onScroll = () => setScrolled(window.scrollY > 24);
        window.addEventListener("scroll", onScroll);
        return () => window.removeEventListener("scroll", onScroll);
    }, []);

    return (
        <header
            data-testid="site-nav"
            className={`fixed top-0 left-0 right-0 z-50 backdrop-blur-xl transition-colors ${
                scrolled
                    ? "bg-bone/85 border-b border-line"
                    : "bg-transparent border-b border-transparent"
            }`}
        >
            <div className="max-w-[1440px] mx-auto px-6 md:px-12 lg:px-20 h-16 md:h-20 flex items-center justify-between">
                <a
                    href="#top"
                    data-testid="nav-logo"
                    className="font-serif text-xl md:text-2xl text-ink tracking-tight"
                >
                    Hanan <span className="italic text-sienna">Hakim</span>
                </a>
                <nav className="hidden md:flex items-center gap-8">
                    {NAV_LINKS.map((l) => (
                        <a
                            key={l.id}
                            href={`#${l.id}`}
                            data-testid={`nav-link-${l.id}`}
                            className="nav-link font-mono text-[11px] uppercase tracking-[0.22em] text-ink-soft hover:text-ink transition-colors"
                        >
                            {l.label}
                        </a>
                    ))}
                </nav>
                <a
                    href={RESUME_URL}
                    target="_blank"
                    rel="noopener noreferrer"
                    data-testid="nav-download-resume"
                    className="hidden sm:inline-flex items-center gap-2 border border-ink px-4 py-2 font-mono text-[11px] uppercase tracking-[0.18em] text-ink hover:bg-ink hover:text-bone transition-colors"
                >
                    <Download size={13} /> Résumé
                </a>
            </div>
        </header>
    );
};

// ───────────────────────────────────────────────────────────────────
// HERO
// ───────────────────────────────────────────────────────────────────

const HERO_LINES = [
    <>Strategy at the</>,
    <>
        intersection of <span className="italic text-sienna">energy</span>,
    </>,
    <>materials &amp; markets.</>,
];

const Hero = () => {
    const sectionRef = useRef(null);
    const { scrollYProgress } = useScroll({
        target: sectionRef,
        offset: ["start start", "end start"],
    });
    const yHeadline = useTransform(scrollYProgress, [0, 1], [0, 110]);
    const yPortrait = useTransform(scrollYProgress, [0, 1], [0, -80]);
    const xGhost = useTransform(scrollYProgress, [0, 1], ["2%", "-16%"]);

    // Pointer-driven 3D tilt on the portrait frame
    const mx = useMotionValue(0);
    const my = useMotionValue(0);
    const rotateX = useSpring(useTransform(my, [-0.5, 0.5], [4.5, -4.5]), {
        stiffness: 140,
        damping: 18,
    });
    const rotateY = useSpring(useTransform(mx, [-0.5, 0.5], [-4.5, 4.5]), {
        stiffness: 140,
        damping: 18,
    });
    const onTilt = (e) => {
        const r = e.currentTarget.getBoundingClientRect();
        mx.set((e.clientX - r.left) / r.width - 0.5);
        my.set((e.clientY - r.top) / r.height - 0.5);
    };
    const resetTilt = () => {
        mx.set(0);
        my.set(0);
    };

    return (
    <section
        id="top"
        ref={sectionRef}
        data-testid="hero-section"
        className="relative overflow-hidden pt-32 md:pt-40 pb-28 md:pb-36"
    >
        {/* Kinetic ghost backdrop — drifts against scroll */}
        <motion.div
            aria-hidden="true"
            style={{ x: xGhost }}
            className="pointer-events-none select-none absolute bottom-2 left-0 whitespace-nowrap font-serif italic text-[16vw] leading-none text-ghost"
        >
            Strategy · Energy · Materials
        </motion.div>

        <div className="relative max-w-[1440px] mx-auto px-6 md:px-12 lg:px-20 grid grid-cols-12 gap-6 md:gap-10">
            <motion.div style={{ y: yHeadline }} className="col-span-12 lg:col-span-7">
                <FadeUp>
                    <Overline accent>
                        Hanan Hakim · London, UK
                    </Overline>
                </FadeUp>
                <h1
                    data-testid="hero-headline"
                    className="font-serif text-[2.6rem] sm:text-6xl md:text-7xl lg:text-[5.2rem] leading-[0.98] tracking-tight text-ink mt-6"
                >
                    {HERO_LINES.map((line, i) => (
                        <span
                            key={i}
                            className="block overflow-hidden pb-[0.08em] -mb-[0.08em]"
                        >
                            <motion.span
                                className="block will-change-transform"
                                initial={{ y: "112%" }}
                                animate={{ y: 0 }}
                                transition={{
                                    duration: 1.05,
                                    delay: 0.15 + i * 0.13,
                                    ease: [0.16, 1, 0.3, 1],
                                }}
                            >
                                {line}
                            </motion.span>
                        </span>
                    ))}
                </h1>
                <FadeUp delay={0.62}>
                    <p
                        data-testid="hero-subheadline"
                        className="mt-8 font-sans text-lg md:text-xl leading-relaxed text-ink-soft max-w-2xl"
                    >
                        I&rsquo;m a strategy consultant and project lead working
                        where deep-tech meets the boardroom — translating
                        materials science, the energy transition and circularity
                        into decision-grade strategy for Forbes Global 2000
                        clients and the UK government.
                    </p>
                </FadeUp>
                <FadeUp delay={0.74}>
                    <div className="mt-10 flex flex-wrap items-center gap-4">
                        <a
                            href="#recruiters"
                            data-testid="hero-cta-contact"
                            className="inline-flex items-center gap-2 bg-ink text-bone px-7 py-4 font-mono text-[12px] uppercase tracking-[0.2em] hover:bg-sienna transition-colors"
                        >
                            Get in touch <ArrowUpRight size={15} />
                        </a>
                        <a
                            href={RESUME_URL}
                            target="_blank"
                            rel="noopener noreferrer"
                            data-testid="hero-cta-resume"
                            className="inline-flex items-center gap-2 border border-ink px-7 py-4 font-mono text-[12px] uppercase tracking-[0.2em] text-ink hover:bg-ink hover:text-bone transition-colors"
                        >
                            Download résumé <Download size={14} />
                        </a>
                        <a
                            href={LINKEDIN_URL}
                            target="_blank"
                            rel="noopener noreferrer"
                            data-testid="hero-cta-linkedin"
                            className="inline-flex items-center gap-2 font-mono text-[12px] uppercase tracking-[0.2em] text-ink-soft hover:text-ink transition-colors underline underline-offset-4 decoration-line"
                        >
                            <Linkedin size={14} /> LinkedIn
                        </a>
                    </div>
                </FadeUp>

                {/* Quick facts strip */}
                <FadeUp delay={0.86}>
                    <div className="mt-14 grid grid-cols-3 gap-x-6 gap-y-6 border-t border-line pt-8 max-w-2xl">
                        <div>
                            <Overline>Currently</Overline>
                            <p className="font-serif text-base md:text-lg text-ink mt-2 leading-snug">
                                Graduated <span className="italic">MSc Imperial</span> — looking for full-time roles
                            </p>
                        </div>
                        <div>
                            <Overline>Based in</Overline>
                            <p className="font-serif text-base md:text-lg text-ink mt-2 leading-snug">
                                London, UK
                            </p>
                        </div>
                        <div>
                            <Overline>Open to</Overline>
                            <p className="font-serif text-base md:text-lg text-ink mt-2 leading-snug">
                                Full-time · 2026
                            </p>
                        </div>
                    </div>
                </FadeUp>
            </motion.div>

            {/* Portrait — clipped-frame reveal, pointer tilt, scroll parallax */}
            <div className="col-span-12 lg:col-span-5 lg:pl-4 mt-12 lg:mt-0">
                <motion.div style={{ y: yPortrait }} className="relative w-full">
                    <div
                        style={{ perspective: 900 }}
                        onMouseMove={onTilt}
                        onMouseLeave={resetTilt}
                    >
                        <motion.div
                            style={{
                                rotateX,
                                rotateY,
                                transformStyle: "preserve-3d",
                            }}
                            className="relative"
                        >
                            <motion.div
                                initial={{ clipPath: "inset(100% 0 0 0)" }}
                                animate={{ clipPath: "inset(0% 0 0 0)" }}
                                transition={{
                                    duration: 1.15,
                                    delay: 0.4,
                                    ease: [0.16, 1, 0.3, 1],
                                }}
                                className="relative aspect-[4/5] w-full max-w-[460px] mx-auto lg:ml-auto bg-bone-soft border border-line overflow-hidden"
                            >
                                <motion.img
                                    src={HEADSHOT_URL}
                                    alt="Portrait of Hanan Hakim"
                                    data-testid="hero-portrait"
                                    initial={{ scale: 1.22 }}
                                    animate={{ scale: 1 }}
                                    transition={{
                                        duration: 1.6,
                                        delay: 0.4,
                                        ease: [0.16, 1, 0.3, 1],
                                    }}
                                    className="absolute inset-0 w-full h-full object-cover object-top grayscale contrast-[1.05] hover:grayscale-0 transition-[filter] duration-700"
                                    loading="eager"
                                />
                                <span className="absolute top-3 left-3 w-3 h-3 border-t border-l border-ink" />
                                <span className="absolute top-3 right-3 w-3 h-3 border-t border-r border-ink" />
                                <span className="absolute bottom-3 left-3 w-3 h-3 border-b border-l border-ink" />
                                <span className="absolute bottom-3 right-3 w-3 h-3 border-b border-r border-ink" />
                            </motion.div>

                            <motion.div
                                initial={{ opacity: 0, y: 16 }}
                                animate={{ opacity: 1, y: 0 }}
                                transition={{ duration: 0.7, delay: 1.05, ease: [0.2, 0.7, 0.2, 1] }}
                                data-testid="hero-proof-card"
                                className="absolute -bottom-6 left-2 sm:left-6 lg:left-0 bg-ink text-bone px-5 py-4 max-w-[280px] shadow-[8px_8px_0_0_rgba(140,41,21,0.9)]"
                            >
                                <div className="flex items-center gap-3">
                                    <span className="relative flex h-2.5 w-2.5">
                                        <span className="absolute inline-flex h-full w-full rounded-full bg-sienna opacity-75 animate-ping" />
                                        <span className="relative inline-flex rounded-full h-2.5 w-2.5 bg-sienna" />
                                    </span>
                                    <p className="font-mono text-[10px] uppercase tracking-[0.22em] text-bone/70">
                                        Available · interviewing
                                    </p>
                                </div>
                                <p className="font-serif text-lg text-bone mt-2 leading-snug">
                                    Strategy, PM &amp;{" "}
                                    <span className="italic text-sienna">
                                        energy / materials
                                    </span>{" "}
                                    roles.
                                </p>
                            </motion.div>
                        </motion.div>
                    </div>

                    <div className="hidden lg:flex items-center gap-3 mt-12 font-mono text-[10px] uppercase tracking-[0.24em] text-ink-soft">
                        <span className="h-px w-10 bg-ink-soft/40" />
                        <span>Photo · 2025</span>
                        <span className="text-sienna">◆</span>
                        <span>London</span>
                    </div>
                </motion.div>
            </div>
        </div>
    </section>
    );
};

// ───────────────────────────────────────────────────────────────────
// ───────────────────────────────────────────────────────────────────
// TRUSTED BY (logo strip)
// ───────────────────────────────────────────────────────────────────

const TrustedBy = () => (
    <section
        data-testid="trusted-by"
        className="border-t border-line py-12 md:py-16"
    >
        <div className="max-w-[1440px] mx-auto px-6 md:px-12 lg:px-20 grid grid-cols-12 gap-6 md:gap-10 items-center">
            <div className="col-span-12 md:col-span-3">
                <Overline accent>Trusted ground —</Overline>
                <p className="font-serif text-2xl md:text-[1.7rem] text-ink mt-2 leading-snug">
                    where the work has{" "}
                    <span className="italic">already landed.</span>
                </p>
            </div>
            <div className="col-span-12 md:col-span-9">
                <div className="group flex flex-wrap items-center gap-x-6 gap-y-5 md:gap-x-10">
                    {TRUSTED_BY.map((org) => (
                        <div
                            key={org.name}
                            className="flex items-center gap-3"
                            data-testid={`trusted-${org.initials}`}
                        >
                            <Logo
                                logoUrl={org.logoUrl}
                                domain={org.domain}
                                initials={org.initials}
                                name={org.name}
                                size={42}
                            />
                            <span className="font-mono text-[11px] uppercase tracking-[0.2em] text-ink-soft">
                                {org.name}
                            </span>
                        </div>
                    ))}
                </div>
            </div>
        </div>
    </section>
);


// FEATURED IMPACT
// ───────────────────────────────────────────────────────────────────

const FeaturedImpact = () => (
    <section
        id="impact"
        data-testid="impact-section"
        className="border-t border-line py-20 md:py-28"
    >
        <div className="max-w-[1440px] mx-auto px-6 md:px-12 lg:px-20">
            <div className="grid grid-cols-12 gap-6 md:gap-10 mb-12 md:mb-16">
                <div className="col-span-12 md:col-span-4">
                    <ChapterHead index="01" overline="01 / Featured Impact">
                        Featured impact, <br />
                        <span className="italic">in numbers.</span>
                    </ChapterHead>
                </div>
                <div className="col-span-12 md:col-span-7 md:col-start-6 self-end">
                    <p className="font-sans text-base md:text-lg text-ink-soft leading-relaxed max-w-xl">
                        Four signals that show how I work: data-rooted,
                        commercially literate and translated into decisions a
                        board can actually make.
                    </p>
                </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 border-t border-ink">
                {METRICS.map((m, i) => (
                    <FadeUp
                        key={m.label}
                        delay={i * 0.06}
                        className={`px-6 md:px-8 py-10 border-b border-line ${
                            i < METRICS.length - 1
                                ? "lg:border-r lg:border-line"
                                : ""
                        }`}
                    >
                        <div
                            data-testid={`metric-card-${i}`}
                            className="h-full flex flex-col"
                        >
                            <p className="font-mono text-[11px] uppercase tracking-[0.22em] text-sienna">
                                0{i + 1}
                            </p>
                            <p className="font-mono text-3xl md:text-4xl text-ink mt-6 tracking-tight">
                                {m.value}
                            </p>
                            <p className="font-serif text-xl text-ink mt-3 italic">
                                {m.label}
                            </p>
                            <p className="font-sans text-sm text-ink-soft leading-relaxed mt-4">
                                {m.body}
                            </p>
                        </div>
                    </FadeUp>
                ))}
            </div>
        </div>
    </section>
);

// ───────────────────────────────────────────────────────────────────
// ABOUT
// ───────────────────────────────────────────────────────────────────

const About = () => (
    <section
        id="about"
        data-testid="about-section"
        className="border-t border-line py-20 md:py-32 bg-bone-soft"
    >
        <div className="max-w-[1440px] mx-auto px-6 md:px-12 lg:px-20 grid grid-cols-12 gap-6 md:gap-10">
            <div className="col-span-12 md:col-span-4 lg:col-span-3">
                <div className="md:sticky md:top-28">
                    <ChapterHead index="02" overline="02 / About">
                        The <span className="italic">why</span> behind the work.
                    </ChapterHead>
                    <div className="hidden md:block mt-10 border-t border-line pt-6 font-mono text-[11px] uppercase tracking-[0.2em] text-ink-soft">
                        <p>Three institutions · one thread —</p>
                        <p className="text-sienna mt-2">
                            chemistry → materials → strategy.
                        </p>
                    </div>
                </div>
            </div>

            <div className="col-span-12 md:col-span-8 md:col-start-6 lg:col-start-6 lg:col-span-7">
                <FadeUp>
                    <p className="font-serif italic text-2xl md:text-[1.7rem] text-ink leading-[1.45]">
                        &ldquo;The most interesting problems aren&rsquo;t purely
                        technical or purely commercial — they live in the
                        translation layer between the two.&rdquo;
                    </p>
                </FadeUp>

                <FadeUp delay={0.1}>
                    <div className="mt-10 space-y-6 font-sans text-base md:text-lg leading-[1.75] text-ink-soft">
                        <p>
                            I started in a chemistry lab in Surabaya, finished
                            my BSc <span className="text-ink">cum laude</span>,
                            and quickly realised the question that lit me up
                            wasn&rsquo;t <em>what</em> we could synthesise — it
                            was <em>which</em> molecules deserved a place in a
                            decarbonised economy, and how to get them there.
                            That curiosity pulled me into R&amp;D at the Nano
                            Center, then into founding{" "}
                            <span className="text-ink">Atomalyst Group</span> —
                            a four-person advisory that ended up running
                            ~£550k of mandates for Forbes Global 2000 clients
                            in chemicals, beauty and aerospace-defence.
                        </p>
                        <p>
                            From Indonesia I moved to London and Imperial
                            College for an MSc in Advanced Materials Science
                            &amp; Engineering, with a Distinction in Machine
                            Learning for Materials. Imperial has been a
                            multiplier: a DESNZ-commissioned climate report
                            with UCL and Cambridge, strategy work with{" "}
                            <span className="text-ink">
                                180 Degrees Consulting
                            </span>{" "}
                            on £41B sectors, and a thesis at the Henry Royce
                            Institute building open-source tools the lab now
                            uses as a standard workflow.
                        </p>
                        <p>
                            What I look for next are roles where I can keep
                            doing exactly this — pairing first-principles
                            technical depth with the discipline of strategy
                            consulting. Project management for energy &amp;
                            sustainability programmes, strategy roles in deep
                            tech or climate, and analyst tracks at firms that
                            take the energy transition seriously. If
                            you&rsquo;re hiring for that intersection, I&rsquo;d
                            love to talk.
                        </p>
                    </div>
                </FadeUp>

                <FadeUp delay={0.2}>
                    <div className="mt-14 border-t border-line pt-10">
                        <Overline>Core competencies</Overline>
                        <div className="mt-6 flex flex-wrap gap-2">
                            {COMPETENCIES.map((c) => (
                                <span
                                    key={c}
                                    data-testid={`competency-${c}`}
                                    className="font-mono text-[11px] uppercase tracking-[0.18em] text-ink border border-line bg-bone px-3 py-2 hover:border-ink transition-colors"
                                >
                                    {c}
                                </span>
                            ))}
                        </div>
                    </div>
                </FadeUp>

                <FadeUp delay={0.28}>
                    <div className="mt-14 border-t border-line pt-10">
                        <Overline>Education</Overline>
                        <div
                            data-testid="education-row"
                            className="mt-6 grid grid-cols-1 sm:grid-cols-3 gap-4"
                        >
                            {EDUCATION.map((ed, i) => (
                                <div
                                    key={ed.school}
                                    data-testid={`education-card-${i}`}
                                    className="group relative rounded-2xl border border-line bg-bone p-5 hover:border-ink hover:-translate-y-0.5 transition-all duration-300"
                                >
                                    <div className="flex items-start justify-between gap-3">
                                        <Logo
                                            logoUrl={ed.logoUrl}
                                            domain={ed.domain}
                                            initials={ed.initials}
                                            name={ed.school}
                                            size={48}
                                        />
                                        <span className="font-mono text-[10px] tracking-[0.18em] text-ink-soft pt-1">
                                            {ed.years}
                                        </span>
                                    </div>
                                    <div className="mt-5">
                                        <span className="font-mono text-[10px] uppercase tracking-[0.22em] text-sienna">
                                            {ed.degree_short}
                                        </span>
                                        <p className="font-serif text-[1.35rem] text-ink mt-2 leading-[1.2]">
                                            {ed.school}
                                        </p>
                                    </div>
                                    <p className="font-sans text-sm text-ink-soft leading-relaxed mt-3">
                                        {ed.detail}
                                    </p>
                                    <p className="font-mono text-[10px] uppercase tracking-[0.22em] text-ink mt-4 pt-3 border-t border-line">
                                        {ed.honour}
                                    </p>
                                </div>
                            ))}
                        </div>
                    </div>
                </FadeUp>
            </div>
        </div>
    </section>
);

// ───────────────────────────────────────────────────────────────────
// ───────────────────────────────────────────────────────────────────
// INSIGHTS
// ───────────────────────────────────────────────────────────────────

const Insights = () => (
    <section
        id="insights"
        data-testid="insights-section"
        className="border-t border-line py-20 md:py-32"
    >
        <div className="max-w-[1440px] mx-auto px-6 md:px-12 lg:px-20">
            <div className="grid grid-cols-12 gap-6 md:gap-10 mb-12 md:mb-16">
                <div className="col-span-12 md:col-span-7">
                    <Overline accent>03 / Insights</Overline>
                    <h2 className="font-serif text-4xl md:text-5xl text-ink mt-4 leading-[1.05]">
                        Notes from the <br />
                        <span className="italic">work itself.</span>
                    </h2>
                </div>
                <div className="col-span-12 md:col-span-5 self-end">
                    <p className="font-sans text-base text-ink-soft leading-relaxed">
                        Short essays on energy policy, deep-tech strategy and the translation layer between the lab and the boardroom.
                    </p>
                </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-3 border-t border-ink">
                {INSIGHTS.map((p, i) => (
                    <FadeUp
                        key={p.slug}
                        delay={i * 0.06}
                        className={`px-0 md:px-6 py-10 ${
                            i < INSIGHTS.length - 1
                                ? "md:border-r border-line"
                                : ""
                        } border-b md:border-b-0 border-line md:border-b-0`}
                    >
                        <Link
                            to={`/insights/${p.slug}`}
                            data-testid={`insight-card-${p.slug}`}
                            className="group block h-full"
                        >
                            <div className="flex items-center justify-between">
                                <span className="font-mono text-[10px] uppercase tracking-[0.22em] text-sienna">
                                    {p.category}
                                </span>
                                <span className="font-mono text-[10px] uppercase tracking-[0.18em] text-ink-soft">
                                    {p.date} · {p.read}
                                </span>
                            </div>
                            <h3 className="font-serif text-2xl md:text-[1.65rem] text-ink mt-6 leading-[1.15] group-hover:text-sienna transition-colors">
                                {p.title}
                            </h3>
                            <p className="font-sans text-sm text-ink-soft leading-relaxed mt-4">
                                {p.dek}
                            </p>
                            <p className="mt-6 inline-flex items-center gap-1.5 font-mono text-[11px] uppercase tracking-[0.22em] text-ink border-b border-ink pb-1 group-hover:text-sienna group-hover:border-sienna transition-colors">
                                Read article
                                <ArrowUpRight size={12} />
                            </p>
                        </Link>
                    </FadeUp>
                ))}
            </div>
        </div>
    </section>
);


// PORTFOLIO
// ───────────────────────────────────────────────────────────────────

const Portfolio = () => (
    <section
        id="portfolio"
        data-testid="portfolio-section"
        className="border-t border-line py-20 md:py-32"
    >
        <div className="max-w-[1440px] mx-auto px-6 md:px-12 lg:px-20">
            <div className="grid grid-cols-12 gap-6 md:gap-10 mb-12 md:mb-20">
                <div className="col-span-12 md:col-span-7">
                    <ChapterHead index="03" overline="03 / Portfolio">
                        Five case studies <br />
                        <span className="italic">that tell the story.</span>
                    </ChapterHead>
                </div>
                <div className="col-span-12 md:col-span-5 self-end">
                    <p className="font-sans text-base text-ink-soft leading-relaxed">
                        Each follows the same shape: a real challenge, the
                        strategy I led, and the result in numbers. No fluff, no
                        retro-fitted narratives.
                    </p>
                </div>
            </div>

            <div className="space-y-0 border-t border-ink">
                {CASES.map((c, i) => (
                    <FadeUp
                        key={c.n}
                        delay={i * 0.05}
                        className="border-b border-line py-12 md:py-16 group"
                    >
                        <div
                            data-testid={`case-study-${c.n}`}
                            className="grid grid-cols-12 gap-6 md:gap-10 transition-transform duration-500 ease-out group-hover:translate-x-1.5"
                        >
                            <div className="col-span-12 md:col-span-3">
                                <p className="font-mono text-[11px] uppercase tracking-[0.22em] text-sienna">
                                    Case {c.n}
                                </p>
                                <p className="font-mono text-[11px] uppercase tracking-[0.18em] text-ink-soft mt-3">
                                    {c.tag}
                                </p>
                            </div>
                            <div className="col-span-12 md:col-span-9">
                                <h3 className="font-serif text-3xl md:text-4xl text-ink leading-[1.1] group-hover:text-sienna transition-colors">
                                    {c.title}
                                </h3>
                                <div className="grid grid-cols-1 md:grid-cols-3 gap-8 mt-8">
                                    <div>
                                        <Overline>Challenge</Overline>
                                        <p className="font-sans text-sm md:text-base text-ink-soft leading-relaxed mt-3">
                                            {c.challenge}
                                        </p>
                                    </div>
                                    <div>
                                        <Overline>Strategy</Overline>
                                        <p className="font-sans text-sm md:text-base text-ink-soft leading-relaxed mt-3">
                                            {c.strategy}
                                        </p>
                                    </div>
                                    <div>
                                        <Overline accent>Result</Overline>
                                        <p className="font-sans text-sm md:text-base text-ink leading-relaxed mt-3">
                                            {c.result}
                                        </p>
                                    </div>
                                </div>
                                {c.slug && (
                                    <Link
                                        to={`/case/${c.slug}`}
                                        data-testid={`case-readmore-${c.slug}`}
                                        className="inline-flex items-center gap-2 mt-8 font-mono text-[11px] uppercase tracking-[0.22em] text-ink hover:text-sienna transition-colors border-b border-ink hover:border-sienna pb-1"
                                    >
                                        Read the full case study
                                        <ArrowUpRight size={13} />
                                    </Link>
                                )}
                            </div>
                        </div>
                    </FadeUp>
                ))}
            </div>
        </div>
    </section>
);

// ───────────────────────────────────────────────────────────────────
// ───────────────────────────────────────────────────────────────────
// LEADERSHIP
// ───────────────────────────────────────────────────────────────────

const Leadership = () => (
    <section
        id="leadership"
        data-testid="leadership-section"
        className="border-t border-line py-20 md:py-32 bg-bone-soft"
    >
        <div className="max-w-[1440px] mx-auto px-6 md:px-12 lg:px-20">
            <div className="grid grid-cols-12 gap-6 md:gap-10 mb-12 md:mb-16">
                <div className="col-span-12 md:col-span-7">
                    <ChapterHead index="04" overline="04 / Leadership">
                        Where I&rsquo;ve led, <br />
                        <span className="italic">organised &amp; shown up.</span>
                    </ChapterHead>
                </div>
                <div className="col-span-12 md:col-span-5 self-end">
                    <p className="font-sans text-base text-ink-soft leading-relaxed">
                        Four organisations that shaped how I think about
                        teams, programmes and turning a stalled room into a
                        delivered outcome.
                    </p>
                </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 md:gap-5">
                {LEADERSHIP.map((l, i) => (
                    <FadeUp key={l.name} delay={i * 0.05}>
                        <article
                            data-testid={`leadership-card-${i}`}
                            className="group bg-bone border border-line rounded-2xl p-6 md:p-8 h-full hover:border-ink hover:-translate-y-0.5 transition-all duration-300"
                        >
                            <div className="flex items-start gap-5">
                                <Logo
                                    logoUrl={l.logoUrl}
                                    domain={l.domain}
                                    initials={l.initials}
                                    name={l.name}
                                    size={56}
                                />
                                <div className="flex-1 min-w-0">
                                    <div className="flex items-baseline justify-between gap-3 flex-wrap">
                                        <span className="font-mono text-[10px] uppercase tracking-[0.22em] text-sienna">
                                            {l.n} · {l.location}
                                        </span>
                                        <span className="font-mono text-[10px] tracking-[0.18em] text-ink-soft">
                                            {l.years}
                                        </span>
                                    </div>
                                    <h3 className="font-serif text-2xl text-ink mt-2 leading-[1.15] group-hover:text-sienna transition-colors">
                                        {l.name}
                                    </h3>
                                    <p className="font-mono text-[11px] uppercase tracking-[0.2em] text-ink mt-2">
                                        {l.role}
                                    </p>
                                </div>
                            </div>
                            <p className="font-sans text-sm md:text-base text-ink-soft leading-relaxed mt-5 pt-5 border-t border-line">
                                {l.detail}
                            </p>
                            {l.links && l.links.length > 0 && (
                                <div
                                    data-testid={`leadership-links-${i}`}
                                    className="mt-5 flex flex-wrap gap-2"
                                >
                                    {l.links.map((lk) => (
                                        <a
                                            key={lk.href}
                                            href={lk.href}
                                            target="_blank"
                                            rel="noopener noreferrer"
                                            className="inline-flex items-center gap-1.5 font-mono text-[10px] uppercase tracking-[0.2em] text-ink border border-line bg-bone px-3 py-1.5 hover:border-ink hover:text-sienna transition-colors"
                                        >
                                            {lk.label}
                                            <ArrowUpRight size={11} />
                                        </a>
                                    ))}
                                </div>
                            )}
                        </article>
                    </FadeUp>
                ))}
            </div>
        </div>
    </section>
);


// PUBLICATIONS + AWARDS
// ───────────────────────────────────────────────────────────────────

const PublicationsAwards = () => (
    <section
        id="publications"
        data-testid="publications-section"
        className="border-t border-line py-20 md:py-32"
    >
        <div className="max-w-[1440px] mx-auto px-6 md:px-12 lg:px-20 grid grid-cols-12 gap-6 md:gap-12">
            {/* Publications */}
            <div className="col-span-12 lg:col-span-7">
                <ChapterHead index="05" overline="05 / Publications">
                    Peer-reviewed <br />
                    <span className="italic">contributions.</span>
                </ChapterHead>

                <div className="mt-10 border-t border-ink">
                    {PUBLICATIONS.map((p, i) => (
                        <FadeUp
                            key={i}
                            delay={i * 0.05}
                            className="border-b border-line py-6 grid grid-cols-12 gap-4"
                        >
                            <div className="col-span-2 font-mono text-[12px] tracking-widest text-sienna pt-1">
                                {p.year}
                            </div>
                            <div className="col-span-10">
                                <p className="font-serif text-xl text-ink leading-snug">
                                    {p.title}
                                </p>
                                <p className="font-mono text-[11px] uppercase tracking-[0.18em] text-ink-soft mt-2">
                                    {p.venue}
                                </p>
                            </div>
                        </FadeUp>
                    ))}
                </div>

                <div className="mt-8 flex flex-wrap gap-3">
                    <a
                        href={SCHOLAR_URL}
                        target="_blank"
                        rel="noopener noreferrer"
                        data-testid="link-google-scholar"
                        className="inline-flex items-center gap-2 border border-ink px-5 py-3 font-mono text-[11px] uppercase tracking-[0.2em] text-ink hover:bg-ink hover:text-bone transition-colors"
                    >
                        <GraduationCap size={14} /> Google Scholar
                    </a>
                    <a
                        href={RESEARCHGATE_URL}
                        target="_blank"
                        rel="noopener noreferrer"
                        data-testid="link-researchgate"
                        className="inline-flex items-center gap-2 border border-ink px-5 py-3 font-mono text-[11px] uppercase tracking-[0.2em] text-ink hover:bg-ink hover:text-bone transition-colors"
                    >
                        <BookOpen size={14} /> ResearchGate
                    </a>
                </div>
            </div>

            {/* Awards */}
            <div className="col-span-12 lg:col-span-5 lg:pl-10 lg:border-l lg:border-line">
                <ChapterHead index="06" overline="06 / Recognition">
                    Awards &amp; <br />
                    <span className="italic">scholarships.</span>
                </ChapterHead>

                <div className="mt-10 border-t border-ink">
                    {AWARDS.map((a, i) => (
                        <FadeUp
                            key={a.title}
                            delay={i * 0.05}
                            className="border-b border-line py-6"
                        >
                            <div className="flex items-baseline justify-between gap-4">
                                <p className="font-serif text-xl text-ink leading-snug">
                                    {a.title}
                                </p>
                                <p className="font-mono text-[12px] tracking-widest text-sienna shrink-0">
                                    {a.year}
                                </p>
                            </div>
                            <p className="font-sans text-sm text-ink-soft leading-relaxed mt-2">
                                {a.detail}
                            </p>
                        </FadeUp>
                    ))}
                </div>
            </div>
        </div>
    </section>
);

// ───────────────────────────────────────────────────────────────────
// AWARDS GALLERY — left/right carousel
// ───────────────────────────────────────────────────────────────────

const AwardsGallery = () => {
    const [index, setIndex] = useState(0);
    const total = AWARDS_GALLERY.length;
    const item = AWARDS_GALLERY[index];

    const next = () => setIndex((i) => (i + 1) % total);
    const prev = () => setIndex((i) => (i - 1 + total) % total);

    useEffect(() => {
        const onKey = (e) => {
            if (e.key === "ArrowRight") next();
            if (e.key === "ArrowLeft") prev();
        };
        window.addEventListener("keydown", onKey);
        return () => window.removeEventListener("keydown", onKey);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [total]);

    return (
        <section
            id="awards-gallery"
            data-testid="awards-gallery"
            className="border-t border-line py-20 md:py-28 bg-bone-soft"
        >
            <div className="max-w-[1440px] mx-auto px-6 md:px-12 lg:px-20">
                <div className="grid grid-cols-12 gap-6 md:gap-10 mb-10 md:mb-14">
                    <div className="col-span-12 md:col-span-7">
                        <Overline accent>Awards · Gallery</Overline>
                        <h2 className="font-serif text-4xl md:text-5xl text-ink mt-4 leading-[1.05]">
                            The proof, <br />
                            <span className="italic">in pictures.</span>
                        </h2>
                    </div>
                    <div className="col-span-12 md:col-span-5 self-end">
                        <p className="font-sans text-base text-ink-soft leading-relaxed">
                            A short visual record — moments and certificates behind the line items in Recognition.
                        </p>
                    </div>
                </div>

                <div className="grid grid-cols-12 gap-6 md:gap-10 items-stretch">
                    {/* Image panel */}
                    <div className="col-span-12 lg:col-span-7 relative">
                        <div className="relative aspect-[4/3] md:aspect-[3/2] bg-bone border border-line overflow-hidden">
                            <img
                                key={item.slug}
                                src={item.image}
                                alt={item.title}
                                data-testid="awards-gallery-image"
                                className="absolute inset-0 w-full h-full object-contain p-3 md:p-6 transition-opacity duration-500"
                            />
                            {/* corner ticks */}
                            <span className="absolute top-3 left-3 w-3 h-3 border-t border-l border-ink" />
                            <span className="absolute top-3 right-3 w-3 h-3 border-t border-r border-ink" />
                            <span className="absolute bottom-3 left-3 w-3 h-3 border-b border-l border-ink" />
                            <span className="absolute bottom-3 right-3 w-3 h-3 border-b border-r border-ink" />
                        </div>
                        {/* Controls */}
                        <div className="mt-4 flex items-center justify-between gap-4">
                            <span
                                data-testid="awards-gallery-counter"
                                className="font-mono text-[11px] uppercase tracking-[0.24em] text-ink-soft"
                            >
                                {String(index + 1).padStart(2, "0")} /{" "}
                                {String(total).padStart(2, "0")}
                            </span>
                            <div className="flex items-center gap-2">
                                <button
                                    onClick={prev}
                                    aria-label="Previous award"
                                    data-testid="awards-gallery-prev"
                                    className="w-11 h-11 border border-ink flex items-center justify-center text-ink hover:bg-ink hover:text-bone transition-colors"
                                >
                                    <ChevronLeft size={16} />
                                </button>
                                <button
                                    onClick={next}
                                    aria-label="Next award"
                                    data-testid="awards-gallery-next"
                                    className="w-11 h-11 border border-ink flex items-center justify-center text-ink hover:bg-ink hover:text-bone transition-colors"
                                >
                                    <ChevronRight size={16} />
                                </button>
                            </div>
                        </div>
                    </div>

                    {/* Copy panel */}
                    <div
                        key={item.slug + "-copy"}
                        className="col-span-12 lg:col-span-5 lg:pl-6 flex flex-col"
                        data-testid="awards-gallery-copy"
                    >
                        <Overline accent className="">
                            {item.year} · Selected award
                        </Overline>
                        <h3 className="font-serif text-3xl md:text-[2.2rem] text-ink mt-4 leading-[1.1]">
                            {item.title}
                        </h3>
                        <p className="font-mono text-[11px] uppercase tracking-[0.2em] text-ink-soft mt-3">
                            {item.location}
                        </p>
                        <p className="font-sans text-base md:text-lg text-ink-soft leading-[1.7] mt-6">
                            {item.description}
                        </p>

                        {/* Thumb rail */}
                        <div className="mt-auto pt-8 border-t border-line">
                            <p className="font-mono text-[10px] uppercase tracking-[0.22em] text-ink-soft mb-3">
                                Quick jump
                            </p>
                            <div className="flex flex-wrap gap-2">
                                {AWARDS_GALLERY.map((g, i) => (
                                    <button
                                        key={g.slug}
                                        onClick={() => setIndex(i)}
                                        data-testid={`awards-gallery-thumb-${i}`}
                                        className={`relative w-12 h-12 border overflow-hidden transition-all ${
                                            i === index
                                                ? "border-sienna ring-2 ring-sienna"
                                                : "border-line hover:border-ink"
                                        }`}
                                    >
                                        <img
                                            src={g.image}
                                            alt={g.title}
                                            className="absolute inset-0 w-full h-full object-cover"
                                        />
                                    </button>
                                ))}
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </section>
    );
};

// ───────────────────────────────────────────────────────────────────
// FOR RECRUITERS / CONTACT
// ───────────────────────────────────────────────────────────────────

const ForRecruiters = () => {
    const [form, setForm] = useState({
        name: "",
        email: "",
        company: "",
        role_interest: "",
        subject: "",
        message: "",
    });
    const [submitting, setSubmitting] = useState(false);

    const handleChange = (e) =>
        setForm((f) => ({ ...f, [e.target.name]: e.target.value }));

    const handleSubmit = async (e) => {
        e.preventDefault();
        if (!form.name.trim() || !form.email.trim() || form.message.trim().length < 5) {
            toast.error("Please complete name, email and a short message.");
            return;
        }
        try {
            setSubmitting(true);
            await axios.post(`${API}/contact`, form);
            toast.success("Message sent. I'll reply within 48 hours.");
            setForm({ name: "", email: "", company: "", role_interest: "", subject: "", message: "" });
        } catch (err) {
            const detail =
                err?.response?.data?.detail || "Couldn't send your message right now.";
            toast.error(`${detail} — opening your email client instead.`);
            const subject = encodeURIComponent(
                `[Personal Site] Opportunity — ${form.role_interest || "Strategy / PM / Sustainability"}`
            );
            const body = encodeURIComponent(
                `Hi Hanan,\n\n${form.message}\n\n— ${form.name}${
                    form.company ? `, ${form.company}` : ""
                }`
            );
            window.location.href = `mailto:${EMAIL}?subject=${subject}&body=${body}`;
        } finally {
            setSubmitting(false);
        }
    };

    return (
        <section
            id="recruiters"
            data-testid="recruiters-section"
            className="relative bg-ink text-bone py-20 md:py-32 overflow-hidden"
        >
            <div className="max-w-[1440px] mx-auto px-6 md:px-12 lg:px-20 grid grid-cols-12 gap-6 md:gap-12 relative">
                <div className="col-span-12 lg:col-span-5">
                    <ChapterHead
                        index="07"
                        overline="07 / Keep in touch"
                        dark
                        titleClassName="lg:text-6xl"
                    >
                        Hiring, mentoring, <br />
                        studying abroad <br />
                        — or just{" "}
                        <span className="italic text-sienna">
                            want to talk?
                        </span>
                    </ChapterHead>
                    <p className="font-sans text-base md:text-lg text-bone/70 leading-relaxed mt-8 max-w-md">
                        I&rsquo;m interviewing for full-time roles starting in 2026, but I&rsquo;m also open to anything outside of work — mentoring sessions, study-abroad chats, deep-tech and climate conversations, or just a friendly hello. Send a short note and I&rsquo;ll reply within 48 hours.
                    </p>

                    <div className="mt-10 space-y-3 font-mono text-[12px] uppercase tracking-[0.18em] text-bone/70">
                        <p className="flex items-center gap-3">
                            <Mail size={14} className="text-sienna" />
                            <a
                                href={`mailto:${EMAIL}`}
                                data-testid="contact-email"
                                className="hover:text-bone transition-colors"
                            >
                                {EMAIL}
                            </a>
                        </p>
                        <p className="flex items-center gap-3">
                            <Phone size={14} className="text-sienna" />
                            +44 7741 452068
                        </p>
                        <p className="flex items-center gap-3">
                            <MapPin size={14} className="text-sienna" />
                            London, UK · open to relocation
                        </p>
                        <p className="flex items-center gap-3">
                            <Linkedin size={14} className="text-sienna" />
                            <a
                                href={LINKEDIN_URL}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="hover:text-bone transition-colors"
                            >
                                linkedin.com/in/hananhakim
                            </a>
                        </p>
                    </div>

                    <div className="mt-10 border-t border-line-strong pt-6">
                        <Overline className="text-bone/50">
                            Open to —
                        </Overline>
                        <ul className="mt-3 font-sans text-sm text-bone/70 leading-relaxed space-y-1.5">
                            <li>— Strategy Analyst / Associate (deep-tech, climate)</li>
                            <li>— Project Manager — energy &amp; sustainability programmes</li>
                            <li>— Sustainability / ESG strategist (energy &amp; materials)</li>
                            <li>— Innovation &amp; R&amp;D strategy at corporates / VCs</li>
                            <li>— Mentoring for study abroad, MSc applications &amp; early-career</li>
                            <li>— Friendly conversations on energy, materials &amp; strategy</li>
                        </ul>
                    </div>
                </div>

                {/* Form */}
                <div className="col-span-12 lg:col-span-7 lg:pl-10 lg:border-l lg:border-line-strong">
                    <form
                        data-testid="contact-form"
                        onSubmit={handleSubmit}
                        className="space-y-6"
                    >
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                            <div>
                                <label className="font-mono text-[11px] uppercase tracking-[0.2em] text-bone/50">
                                    Name *
                                </label>
                                <input
                                    name="name"
                                    value={form.name}
                                    onChange={handleChange}
                                    data-testid="contact-name-input"
                                    required
                                    className="w-full bg-transparent border-b border-line-strong py-3 mt-2 font-sans text-base text-bone placeholder:text-bone/30 focus:outline-none focus:border-sienna transition-colors"
                                    placeholder="Your full name"
                                />
                            </div>
                            <div>
                                <label className="font-mono text-[11px] uppercase tracking-[0.2em] text-bone/50">
                                    Email *
                                </label>
                                <input
                                    name="email"
                                    type="email"
                                    value={form.email}
                                    onChange={handleChange}
                                    data-testid="contact-email-input"
                                    required
                                    className="w-full bg-transparent border-b border-line-strong py-3 mt-2 font-sans text-base text-bone placeholder:text-bone/30 focus:outline-none focus:border-sienna transition-colors"
                                    placeholder="you@company.com"
                                />
                            </div>
                            <div>
                                <label className="font-mono text-[11px] uppercase tracking-[0.2em] text-bone/50">
                                    Company
                                </label>
                                <input
                                    name="company"
                                    value={form.company}
                                    onChange={handleChange}
                                    data-testid="contact-company-input"
                                    className="w-full bg-transparent border-b border-line-strong py-3 mt-2 font-sans text-base text-bone placeholder:text-bone/30 focus:outline-none focus:border-sienna transition-colors"
                                    placeholder="Where you're hiring from"
                                />
                            </div>
                            <div>
                                <label className="font-mono text-[11px] uppercase tracking-[0.2em] text-bone/50">
                                    Role / Team
                                </label>
                                <input
                                    name="role_interest"
                                    value={form.role_interest}
                                    onChange={handleChange}
                                    data-testid="contact-role-input"
                                    className="w-full bg-transparent border-b border-line-strong py-3 mt-2 font-sans text-base text-bone placeholder:text-bone/30 focus:outline-none focus:border-sienna transition-colors"
                                    placeholder="Strategy Analyst, PM, ESG…"
                                />
                            </div>
                        </div>
                        <div>
                            <label className="font-mono text-[11px] uppercase tracking-[0.2em] text-bone/50">
                                Subject
                            </label>
                            <input
                                name="subject"
                                value={form.subject}
                                onChange={handleChange}
                                data-testid="contact-subject-input"
                                maxLength={200}
                                className="w-full bg-transparent border-b border-line-strong py-3 mt-2 font-sans text-base text-bone placeholder:text-bone/30 focus:outline-none focus:border-sienna transition-colors"
                                placeholder="e.g. Mentoring chat · Strategy role at McKinsey · just saying hi"
                            />
                        </div>
                        <div>
                            <label className="font-mono text-[11px] uppercase tracking-[0.2em] text-bone/50">
                                Message *
                            </label>
                            <textarea
                                name="message"
                                value={form.message}
                                onChange={handleChange}
                                data-testid="contact-message-input"
                                rows={5}
                                required
                                minLength={5}
                                className="w-full bg-transparent border-b border-line-strong py-3 mt-2 font-sans text-base text-bone placeholder:text-bone/30 focus:outline-none focus:border-sienna transition-colors resize-none"
                                placeholder="A few lines on the team, the problem and the timeline."
                            />
                        </div>
                        <div className="flex flex-wrap items-center gap-4 pt-4">
                            <button
                                type="submit"
                                disabled={submitting}
                                data-testid="contact-submit-button"
                                className="inline-flex items-center gap-2 bg-sienna text-bone px-8 py-4 font-mono text-[12px] uppercase tracking-[0.2em] hover:bg-sienna-hover transition-colors disabled:opacity-50"
                            >
                                {submitting ? "Sending…" : "Send message"}{" "}
                                <Send size={14} />
                            </button>
                            <a
                                href={`mailto:${EMAIL}?subject=${encodeURIComponent(
                                    "[Personal Site] Opportunity"
                                )}`}
                                data-testid="contact-mailto-fallback"
                                className="font-mono text-[11px] uppercase tracking-[0.2em] text-bone/60 hover:text-bone underline underline-offset-4 decoration-line-strong"
                            >
                                Or open in your mail client →
                            </a>
                        </div>
                    </form>
                </div>
            </div>
        </section>
    );
};

// ───────────────────────────────────────────────────────────────────
// KEYWORDS STRIP (SEO hint) + FOOTER
// ───────────────────────────────────────────────────────────────────

const KeywordStrip = () => (
    <div
        aria-hidden="false"
        className="relative bg-ink text-bone/40 border-t border-line-strong overflow-hidden py-5"
    >
        <div className="flex whitespace-nowrap marquee-slow gap-12 font-mono text-[11px] uppercase tracking-[0.3em]">
            {[...KEYWORDS, ...KEYWORDS].map((k, i) => (
                <span key={i} className="flex items-center gap-12">
                    {k} <span className="text-sienna">◆</span>
                </span>
            ))}
        </div>
    </div>
);

const Footer = () => (
    <footer
        data-testid="site-footer"
        className="bg-ink text-bone/60 py-12 border-t border-line-strong"
    >
        <div className="max-w-[1440px] mx-auto px-6 md:px-12 lg:px-20 grid grid-cols-12 gap-6">
            <div className="col-span-12 md:col-span-6">
                <p className="font-serif text-2xl text-bone">
                    Hanan <span className="italic text-sienna">Hakim</span>
                </p>
                <p className="font-mono text-[11px] uppercase tracking-[0.2em] mt-3">
                    Strategy · Energy Transition · Advanced Materials
                </p>
            </div>
            <div className="col-span-12 md:col-span-6 md:text-right font-mono text-[11px] uppercase tracking-[0.2em] flex flex-col md:items-end gap-2">
                <p>© {new Date().getFullYear()} Hanan Hakim. London, UK.</p>
                <p className="text-bone/40">
                    Built for anyone who wants to keep in touch — recruiters, mentees, collaborators.
                </p>
            </div>
        </div>
    </footer>
);

// ───────────────────────────────────────────────────────────────────
// MAIN
// ───────────────────────────────────────────────────────────────────

export default function PersonalBrand() {
    useEffect(() => {
        document.title =
            "Hanan Hakim — Strategy · Energy Transition · Advanced Materials";

        // Lenis momentum scrolling (anchors handled smoothly too)
        const lenis = new Lenis({
            duration: 1.15,
            smoothWheel: true,
            anchors: true,
        });
        let rafId;
        const raf = (time) => {
            lenis.raf(time);
            rafId = requestAnimationFrame(raf);
        };
        rafId = requestAnimationFrame(raf);
        return () => {
            cancelAnimationFrame(rafId);
            lenis.destroy();
        };
    }, []);

    return (
        <main data-testid="personal-brand-page" className="relative bg-bone text-ink">
            <ScrollProgress />
            <Nav />
            <Hero />
            <EditorialMarquee />
            <TrustedBy />
            <FeaturedImpact />
            <About />
            {/* <Insights />  — Archived: enable when ready to publish */}
            <Portfolio />
            <Leadership />
            <PublicationsAwards />
            <AwardsGallery />
            <ForRecruiters />
            <KeywordStrip />
            <Footer />
        </main>
    );
}
